/**
 * components/clients/ClientSelectorForMerge.tsx
 *
 * Search and select a second client to merge with the current one.
 * Excludes the current client and already-deleted clients.
 * Created Apr 2026.
 * Fix Apr 2026: filter and displayName now include legacy c.name so older records
 *   are searchable and properly labelled.
 */

import { useState, useEffect } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import ClientAvatar from '../ui/ClientAvatar';
import type { Database } from '../../types/database';

type Client = Database['public']['Tables']['clients']['Row'];

interface ClientSelectorForMergeProps {
  userId: string;
  excludeClientId: string;
  onSelect: (client: Client) => void;
}

export default function ClientSelectorForMerge({
  userId,
  excludeClientId,
  onSelect,
}: ClientSelectorForMergeProps) {
  const [query, setQuery] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', userId)
        .is('deleted_at', null)
        .neq('id', excludeClientId)
        .order('last_name', { ascending: true });
      setClients(data || []);
      setLoading(false);
    };
    load();
  }, [userId, excludeClientId]);

  const filtered = query.trim()
    ? clients.filter((c) => {
        const q = query.toLowerCase();
        return (
          c.first_name?.toLowerCase().includes(q) ||
          c.last_name?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q) ||
          c.name?.toLowerCase().includes(q)
        );
      })
    : clients;

  const displayName = (c: Client) =>
    [c.last_name, c.first_name].filter(Boolean).join(' ') || c.name || c.email || 'Client';

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher par nom ou email..."
          className="w-full pl-9 pr-3 py-2 border border-neutral-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-neutral-500 text-center py-6">
          {query ? 'Aucun client trouvé' : 'Aucun autre client'}
        </p>
      ) : (
        <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
          {filtered.map((c) => (
            <li key={c.id}>
              <button
                onClick={() => onSelect(c)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-neutral-50 border border-transparent hover:border-neutral-200 transition-all text-left"
              >
                <ClientAvatar displayName={displayName(c)} size="sm" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-neutral-900 truncate">
                    {displayName(c)}
                  </p>
                  {c.email && (
                    <p className="text-xs text-neutral-500 truncate">{c.email}</p>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
